
import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PenSquare, User, Phone, Mail, Save, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";

interface ProfileSectionProps {
  userName: string;
  userEmail: string;
  userPhone: string;
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({
  userName,
  userEmail,
  userPhone
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(userName);
  const [phone, setPhone] = useState(userPhone);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setName(userName);
    setPhone(userPhone);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: "Erreur",
        description: "Le nom ne peut pas être vide.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        throw userError;
      }
      
      const { error } = await supabase.auth.updateUser({
        data: { 
          full_name: name,
          phone: phone
        }
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Succès",
        description: "Vos informations ont été mises à jour.",
      });
      
      setIsEditing(false);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Une erreur s'est produite lors de la mise à jour.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="md:col-span-2">
      <Card className="p-0 overflow-hidden shadow-md border-0 dark:border dark:border-gray-800 rounded-xl dark:bg-[#121624]">
        <div className="p-6 bg-white dark:bg-[#121624] border-b border-gray-100 dark:border-[rgba(60,63,84,0.6)]">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Profil</h2>
            {isEditing ? (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex items-center gap-2 border-gray-300 dark:border-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-red-600 dark:hover:text-red-400 transition-colors duration-200"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  <X size={16} />
                  Annuler
                </Button>
                <Button 
                  variant="default" 
                  size="sm"
                  className="flex items-center gap-2 bg-[#8b5cf6] hover:bg-[#7c3aed] dark:bg-[#a78bfa] dark:hover:bg-[#8b5cf6] text-white transition-colors duration-200"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  <Save size={16} />
                  {isSaving ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </div>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                className="flex items-center gap-2 border-gray-300 dark:border-[#a78bfa] dark:text-[#a78bfa] hover:bg-gray-50 dark:hover:bg-[rgba(139,92,246,0.2)] hover:text-[#8b5cf6] dark:hover:text-[#a78bfa] transition-colors duration-200"
                onClick={handleEdit}
              >
                <PenSquare size={16} />
                Modifier
              </Button>
            )}
          </div>
        </div>
        
        <div className="py-6 px-6 dark:bg-[#121624] space-y-6">
          {/* Nom complet */}
          <div className="group transition-all duration-200 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40">
            <div className="flex items-center mb-3">
              <User size={20} className="mr-3 text-gray-500 dark:text-gray-400 group-hover:text-[#8b5cf6] dark:group-hover:text-[#a78bfa] transition-colors duration-200" />
              <h3 className="font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors duration-200">Nom Complet</h3>
            </div>
            {isEditing ? (
              <div className="ml-9">
                <Label htmlFor="name" className="sr-only">Nom complet</Label>
                <Input 
                  id="name" 
                  type="text" 
                  value={name} 
                  onChange={(e) => setName(e.target.value)}
                  className="border-gray-300 dark:border-gray-700 dark:text-gray-200 focus:ring-[#8b5cf6] focus:border-[#8b5cf6] dark:focus:ring-[#a78bfa] dark:focus:border-[#a78bfa] dark:bg-gray-800/50"
                  placeholder="Votre nom complet"
                />
              </div>
            ) : (
              <p className="ml-9 text-gray-600 dark:text-gray-300 font-medium">{userName}</p>
            )}
          </div>
          
          <Separator className="bg-gray-100 dark:bg-[rgba(60,63,84,0.6)]" />
          
          {/* Numéro de téléphone */}
          <div className="group transition-all duration-200 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40">
            <div className="flex items-center mb-3">
              <Phone size={20} className="mr-3 text-gray-500 dark:text-gray-400 group-hover:text-[#8b5cf6] dark:group-hover:text-[#a78bfa] transition-colors duration-200" />
              <h3 className="font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors duration-200">Numéro De Téléphone</h3>
            </div>
            {isEditing ? (
              <div className="ml-9">
                <Label htmlFor="phone" className="sr-only">Numéro de téléphone</Label>
                <Input 
                  id="phone" 
                  type="tel" 
                  value={phone} 
                  onChange={(e) => setPhone(e.target.value)}
                  className="border-gray-300 dark:border-gray-700 dark:text-gray-200 focus:ring-[#8b5cf6] focus:border-[#8b5cf6] dark:focus:ring-[#a78bfa] dark:focus:border-[#a78bfa] dark:bg-gray-800/50"
                  placeholder="Votre numéro de téléphone"
                />
              </div>
            ) : (
              <p className="ml-9 text-gray-600 dark:text-gray-300 font-medium">{userPhone}</p>
            )}
          </div>
          
          <Separator className="bg-gray-100 dark:bg-[rgba(60,63,84,0.6)]" />
          
          {/* Email */}
          <div className="group transition-all duration-200 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40">
            <div className="flex items-center mb-3">
              <Mail size={20} className="mr-3 text-gray-500 dark:text-gray-400 group-hover:text-[#8b5cf6] dark:group-hover:text-[#a78bfa] transition-colors duration-200" />
              <h3 className="font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors duration-200">Email</h3>
            </div>
            <p className="ml-9 text-gray-600 dark:text-gray-300 font-medium">{userEmail}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};
